import React from "react";
import styles from "./FixedBundleField.module.css";

// ─── Types ────────────────────────────────────────────────────────────────────
// Defined locally so @codway-dev/ui stays free of @codway-dev/shared dependencies.
// The shared package exports structurally-compatible types that consumers spread
// into these props.

export interface BundleStyles {
  backgroundColor?: string;
  borderStyle?: "solid" | "dashed" | "dotted" | "none";
  borderWidth?: number;
  borderColor?: string;
  activeBorderColor?: string;
  borderRadius?: number;
}

export interface FixedBundleItem {
  id: string;
  productTitle: string;
  imageUrl?: string;
  quantity: number;
  // Per-item catalog price label, when the merchant configured one.
  // Omitted when blank — the qty chip alone renders.
  priceLabel?: string;
}

export interface FixedBundleFieldProps {
  items: FixedBundleItem[];
  title?: string;
  description?: string;
  // Optional cover image for the whole bundle; rendered above the title.
  imageUrl?: string;
  totalPriceLabel: string;
  compareAtLabel?: string;
  savingsLabel?: string;
  styles?: BundleStyles;

  selected: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function bundleCssVars(s?: BundleStyles): React.CSSProperties {
  if (!s) return {};
  return {
    "--bun-background-color": s.backgroundColor,
    "--bun-border-color": s.borderColor,
    "--bun-active-border-color": s.activeBorderColor,
    "--bun-border-style": s.borderStyle,
    "--bun-border-width":
      s.borderWidth != null ? `${s.borderWidth}px` : undefined,
    "--bun-border-radius":
      s.borderRadius != null ? `${s.borderRadius}px` : undefined,
  } as React.CSSProperties;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const FixedBundleField: React.FC<FixedBundleFieldProps> = ({
  items,
  title,
  description,
  imageUrl,
  totalPriceLabel,
  compareAtLabel,
  savingsLabel,
  styles: bundleStyles,
  selected,
  onToggle,
  disabled = false,
}) => {
  const handleToggle = () => {
    if (disabled) return;
    onToggle();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <div
      role="checkbox"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      data-disabled={disabled || undefined}
      className={`${styles.card} ${selected ? styles.cardSelected : ""}`}
      style={bundleCssVars(bundleStyles)}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
    >
      <div
        className={`${styles.radio} ${selected ? styles.radioSelected : ""}`}
      >
        {/* Always rendered (visibility toggled via CSS) so the radio
            container never matches `div:empty` — storefronts often
            ship a reset that hides empty divs and would otherwise
            wipe out the unchecked-state indicator. */}
        <span className={styles.radioInner} aria-hidden="true" />
      </div>

      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          aria-hidden="true"
          className={styles.coverImage}
        />
      )}

      <div className={styles.header}>
        {title && <span className={styles.title}>{title}</span>}
        {description && (
          <span className={styles.description}>{description}</span>
        )}
      </div>

      <ul className={styles.itemList}>
        {items.map((item) => (
          <li key={item.id} className={styles.itemRow}>
            {item.imageUrl ? (
              <img
                src={item.imageUrl}
                alt=""
                aria-hidden="true"
                className={styles.itemImage}
              />
            ) : (
              <div className={styles.itemImagePlaceholder} aria-hidden="true" />
            )}
            <span className={styles.itemTitle}>{item.productTitle}</span>
            <span className={styles.itemQty}>×{item.quantity}</span>
            {item.priceLabel && (
              <span className={styles.itemPrice}>{item.priceLabel}</span>
            )}
          </li>
        ))}
      </ul>

      <div className={styles.footer}>
        <div className={styles.priceRow}>
          {compareAtLabel && (
            <span className={styles.compareAt}>{compareAtLabel}</span>
          )}
          <span className={styles.total}>{totalPriceLabel}</span>
        </div>
        {savingsLabel && (
          <span className={styles.savingsBadge}>Ahorra {savingsLabel}</span>
        )}
      </div>
    </div>
  );
};
